import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { array, object, string } from "yup";

import type { CuratedPlaceOfSafety } from "@thalamos/common";
import type { CreatePlaceOfSafetyRequest } from "@thalamos/aspire-common";
import { isValidEmail } from "@thalamos/aspire-common";

import { api, apiHooks } from "~/api.js";
import {
  Button,
  Dropdown,
  FormLabel,
  HelperText,
  PopupDialog,
  renderErrorToast,
  renderSuccessToast,
  TextField,
  Typeahead,
} from "~/components/design-system/index.js";

const posTypes = [
  "hbPos136",
  "emergencyDepartment",
  "returnedToMentalHealthUnit",
  "careHome",
  "custody",
  "privateHome",
  "other",
];

const requiredEmail = string().email().required("Email is required");
const placeOfSafetySchema = object({
  name: string().required(),
  address: string().required(),
  postalCode: string().required(),
  odsCode: string().optional(),
  posType: string().oneOf(posTypes).optional(),
  organisationId: string().optional(),
  associatedEmails: array().of(requiredEmail).optional(),
  trustId: string().uuid("Invalid Trust ID").required("Trust is required"),
});

export function CreateEditPosModal({
  pos,
  open,
  onClose,
  refetchPos,
}: {
  pos?: CuratedPlaceOfSafety;
  open: boolean;
  onClose: () => void;
  refetchPos: () => {};
}) {
  const { t } = useTranslation();
  const title = pos ? t("pages.posEdit.title") : t("pages.posCreate.title");
  const [newEmail, setNewEmail] = useState<string>("");

  const [trustQuery, setTrustQuery] = useState<string>(
    typeof pos?.trustName === "string" ? pos.trustName : "",
  );
  const [{ data: trustResults }] = apiHooks.trust.search(trustQuery, 10, 0);
  const subcopy = pos
    ? t("pages.posEdit.subcopy")
    : t("pages.posCreate.subcopy");
  const initialValues = {
    name: pos?.name ?? "",
    address: pos?.address ?? "",
    postalCode: pos?.postalCode ?? "",
    odsCode: pos?.odsCode ?? undefined,
    posType: pos?.posType ?? undefined,
    associatedEmails: pos?.associatedEmails ?? undefined,
    organisationId: pos?.organisationId ?? undefined,
    trustId: pos?.trustId ?? "",
    config: {
      s136ExtensionFormEnabled: pos?.config?.s136ExtensionFormEnabled ?? false,
      outcomeFormEnabled: pos?.config?.outcomeFormEnabled ?? false,
    },
  };

  return (
    <PopupDialog open={open} onClose={onClose}>
      <DialogTitle>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Formik<CreatePlaceOfSafetyRequest>
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          const cleanedValues: CreatePlaceOfSafetyRequest = {
            ...values,
            odsCode: values.odsCode ?? undefined,
            posType: values.posType ?? undefined,
            associatedEmails: values.associatedEmails ?? undefined,
            organisationId: values.organisationId ?? undefined,
            trustId: values.trustId ?? null,
          };

          if (pos) {
            try {
              const result = await api.placeOfSafety.update(
                pos.id,
                cleanedValues,
              );
              if (result.status === 204) {
                renderSuccessToast({
                  message: t("pages.posEdit.successMessage"),
                });
              } else {
                renderErrorToast({
                  message: t("pages.posEdit.errorMessage"),
                });
              }
            } catch (_error) {
              renderErrorToast({
                message: t("pages.posEdit.errorMessage"),
              });
            }
          } else {
            try {
              const result = await api.placeOfSafety.create(
                v4(),
                cleanedValues,
              );
              if (result.status === 204) {
                renderSuccessToast({
                  message: t("pages.posCreate.successMessage", {
                    posName: values.name,
                  }),
                });
              } else {
                renderErrorToast({
                  message: t("pages.posCreate.errorMessage"),
                });
              }
            } catch (_error) {
              renderErrorToast({
                message: t("pages.posCreate.errorMessage"),
              });
            }
          }

          setSubmitting(false);
          onClose();
          refetchPos();
        }}
        validationSchema={placeOfSafetySchema}
      >
        {(formikData) => {
          const { values, setValues, errors, touched, submitForm } = formikData;
          return (
            <>
              <DialogContent>
                <Typography>{subcopy}</Typography>
                <Box sx={{ maxWidth: "600px", marginBottom: "2em" }}>
                  <FormLabel
                    label={t("pages.posCreate.name")}
                    error={touched.name && !!errors.name}
                  />
                  <TextField
                    name={"name"}
                    useFullWidth={true}
                    value={values.name}
                    onChange={(e) => {
                      setValues({
                        ...values,
                        name: e,
                      });
                    }}
                  />
                  {errors.name && touched.name && (
                    <Box sx={{ mb: 4 }}>
                      <HelperText errorMessage={errors.name as string} />
                    </Box>
                  )}

                  <FormLabel
                    label={t("pages.posCreate.address")}
                    error={touched.address && !!errors.address}
                  />
                  <TextField
                    name={"address"}
                    useFullWidth={true}
                    value={values.address}
                    onChange={(e) => {
                      setValues({
                        ...values,
                        address: e,
                      });
                    }}
                  />
                  {errors.address && touched.address && (
                    <Box sx={{ mb: 4 }}>
                      <HelperText errorMessage={errors.address as string} />
                    </Box>
                  )}

                  <FormLabel
                    label={t("pages.posCreate.postcode")}
                    error={touched.postalCode && !!errors.postalCode}
                  />
                  <TextField
                    name={"postalCode"}
                    value={values.postalCode}
                    onChange={(e) => {
                      setValues({
                        ...values,
                        postalCode: e,
                      });
                    }}
                  />
                  {errors.postalCode && touched.postalCode && (
                    <Box sx={{ mb: 4 }}>
                      <HelperText errorMessage={errors.postalCode as string} />
                    </Box>
                  )}

                  <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                    <Box sx={{ flex: 2 }}>
                      <FormLabel
                        label={t("pages.posCreate.type")}
                        error={touched.posType && !!errors.posType}
                      />
                      <Dropdown
                        name={"type"}
                        selectedValue={values.posType}
                        values={[
                          { value: "hbPos136", label: "HBPOS (136 Suite)" },
                          {
                            value: "emergencyDepartment",
                            label: "Emergency Department (A&E)",
                          },
                          {
                            value: "returnedToMentalHealthUnit",
                            label: "Returned to Mental Health Unit",
                          },
                          {
                            value: "careHome",
                            label: "Care Home",
                          },
                          {
                            value: "custody",
                            label: "Custody (exceptional circumstances)",
                          },
                          {
                            value: "privateHome",
                            label: "Private Home",
                          },
                          {
                            value: "other",
                            label: "Other",
                          },
                        ]}
                        onChange={(e) => setValues({ ...values, posType: e })}
                      />
                      {errors.posType && touched.posType && (
                        <Box sx={{ mb: 4 }}>
                          <HelperText errorMessage={errors.posType as string} />
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <FormLabel
                        label={t("pages.posCreate.odsCode")}
                        error={touched.odsCode && !!errors.odsCode}
                      />
                      <TextField
                        name={"odsCode"}
                        value={values.odsCode}
                        onChange={(e) => {
                          setValues({
                            ...values,
                            odsCode: e || undefined,
                          });
                        }}
                      />
                      {errors.odsCode && touched.odsCode && (
                        <Box sx={{ mb: 4 }}>
                          <HelperText errorMessage={errors.odsCode as string} />
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <Box sx={{ flex: 2 }}>
                      <FormLabel
                        label={t("pages.posCreate.email")}
                        error={
                          touched.associatedEmails && !!errors.associatedEmails
                        }
                      />
                      <TextField
                        name={"addEmail"}
                        value={newEmail}
                        useFullWidth={true}
                        onChange={(e) => {
                          setNewEmail(e);
                        }}
                      />
                    </Box>

                    <Button
                      disabled={newEmail === ""}
                      label={t("pages.posCreate.addEmail")}
                      onClick={() => {
                        if (!isValidEmail(newEmail)) {
                          renderErrorToast({
                            message: t("pages.posCreate.invalidEmail"),
                          });
                          return;
                        }
                        setValues({
                          ...values,
                          associatedEmails: [
                            ...(values.associatedEmails || []),
                            newEmail.trim(),
                          ],
                        });
                        setNewEmail("");
                      }}
                    />
                  </Box>
                  <FormLabel
                    label={t("pages.posCreate.addedEmails")}
                    error={
                      touched.associatedEmails && !!errors.associatedEmails
                    }
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "1em",
                      minHeight: "5em",
                      marginBottom: "1em",
                    }}
                  >
                    {values.associatedEmails?.map((email, index) => (
                      <Chip
                        key={index}
                        label={email}
                        variant="outlined"
                        onDelete={() => {
                          setValues({
                            ...values,
                            associatedEmails: values.associatedEmails?.filter(
                              (e) => e !== email,
                            ),
                          });
                        }}
                      />
                    ))}
                  </Stack>
                  <FormLabel
                    label={t("pages.posCreate.emhaOrgId")}
                    error={touched.organisationId && !!errors.organisationId}
                  />
                  <TextField
                    name={"organisationId"}
                    useFullWidth={true}
                    value={values.organisationId}
                    onChange={(e) => {
                      setValues({
                        ...values,
                        organisationId: e || undefined,
                      });
                    }}
                    subtext={t("pages.posCreate.orgIdExplainer")}
                  />
                  <Stack
                    sx={{
                      mt: 2,
                    }}
                  >
                    <Typeahead
                      disabled={false}
                      inputValue={trustQuery}
                      onInputChange={(input) => {
                        const value = typeof input === "string" ? input : "";
                        setTrustQuery(value);

                        const match = (trustResults?.results ?? []).find(
                          (t) => t.name === value,
                        );

                        setValues({
                          ...values,
                          trustId: match ? match.id : undefined,
                        });
                      }}
                      options={(trustResults?.results ?? []).map((t) => ({
                        label: t.name,
                        value: t.id,
                      }))}
                      name="trust"
                      label="Trust"
                      errorMessage={
                        errors.trustId && touched.trustId
                          ? errors.trustId
                          : undefined
                      }
                    />
                  </Stack>

                  {/* Feature Flags Section */}
                  <Box sx={{ mt: 3 }}>
                    <FormLabel label={t("pages.posCreate.featureFlags")} />
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              values.config?.s136ExtensionFormEnabled ?? false
                            }
                            onChange={(e) => {
                              setValues({
                                ...values,
                                config: {
                                  ...values.config,
                                  s136ExtensionFormEnabled: e.target.checked,
                                  outcomeFormEnabled:
                                    values.config?.outcomeFormEnabled ?? false,
                                },
                              });
                            }}
                          />
                        }
                        label={t(
                          "pages.posCreate.s136ExtensionFormEnabledLabel",
                        )}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.config?.outcomeFormEnabled ?? false}
                            onChange={(e) => {
                              setValues({
                                ...values,
                                config: {
                                  ...values.config,
                                  s136ExtensionFormEnabled:
                                    values.config?.s136ExtensionFormEnabled ??
                                    false,
                                  outcomeFormEnabled: e.target.checked,
                                },
                              });
                            }}
                          />
                        }
                        label={t("pages.posCreate.outcomeFormEnabledLabel")}
                      />
                    </Stack>
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions sx={{ justifyContent: "space-between" }}>
                <Button
                  label={t("buttonLabels.cancel")}
                  onClick={onClose}
                  variant="outlined"
                />
                <Button
                  label={t("buttonLabels.confirm")}
                  onClick={submitForm}
                  disabled={!formikData.isValid}
                />
              </DialogActions>
            </>
          );
        }}
      </Formik>
    </PopupDialog>
  );
}
